import useUser from '@hooks/auth/useUser';
import useLoading from '@hooks/ui/useLoading';
import useProject from '@hooks/useProject';
import { PaymentMethod } from '@models/order';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { loadTossPayments, TossPaymentsWidgets } from '@tosspayments/tosspayments-sdk';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

function useTossPg() {
    const [widgets, setWidgets] = useState<TossPaymentsWidgets|null>(null);
    const { user } = useUser();
    const project = useProject();
    const state = useCalculatedRequest();
    const dispatch = useDispatch();
    const [agreed, setAgreed] = useState<boolean>(true);
    const { stopLoading } = useLoading();

    useEffect(() => {
        async function fetchPaymentWidgets() {
            if (user && project?.pgVendor === 'TOSS' && project?.tossClientKey) {
                // ------  결제위젯 초기화 ------
                const tossPayments = await loadTossPayments(project.tossClientKey);
                // 회원 결제
                const _widgets = tossPayments.widgets({
                    customerKey: user.barcodeNo,
                });
                setWidgets(_widgets);
            }
        }
        fetchPaymentWidgets();
    }, [project, user]);

    const resolvePaymentMethod = (code: string): PaymentMethod => {
        switch (code) {
        case 'TOSSPAY': return 'TOSS_PAY';
        case 'KAKAOPAY': return 'KAKAO_PAY';
        case 'PAYCO': return 'PAYCO';
        case 'MOBILE_PHONE': return 'MOBILE_PAYMENT';
        default: return 'CREDIT_CARD';
        }
    };

    useEffect(() => {
        async function renderPaymentWidgets() {
            if (widgets == null) {
                return;
            }
            await widgets.setAmount({
                currency: 'KRW',
                value: state.pgAmount,
            });
            const [paymentMethodWidget, agreementWidget] = await Promise.all([
                widgets.renderPaymentMethods({
                    selector: '#payment-method',
                    variantKey: 'DEFAULT',
                }),
                widgets.renderAgreement({
                    selector: '#agreement',
                    variantKey: 'AGREEMENT',
                }),
            ]);
            // 결제 수단 이벤트 구독
            paymentMethodWidget.on('paymentMethodSelect', ({ code }) => {
                const paymentMethod = resolvePaymentMethod(code);
                dispatch(orderActions.setPaymentMethod(paymentMethod));
            });
            agreementWidget.on('agreementStatusChange', ({ agreedRequiredTerms }) => {
                setAgreed(agreedRequiredTerms);
            });
        }
        renderPaymentWidgets();
    }, [widgets, state]);

    const handleTossPg = async (orderNo: string) => {
        if (!agreed) {
            toast.info('약관에 동의해주세요');
            stopLoading();
            return;
        }
        try {
            let orderName = state.orderLines[0].itemName;
            if (state.orderLines.length === 1) {
                orderName += ` ${state.orderLines[0].quantity}개`;
            } else {
                const itemCount = state.orderLines.map((it) => it.quantity)
                    .reduce((acc, cur) => acc + cur, 0);
                orderName += `외 ${itemCount - 1}개`;
            }
            console.log('toss 결제요청 시도');
            await widgets?.requestPayment({
                orderId: orderNo,
                orderName,
                successUrl: `${window.location.origin}/order/toss/success`,
                failUrl: `${window.location.origin}/order/toss/fail`,
            });
        } catch (e) {
            console.error(e);
            stopLoading();
        }
    };

    return {
        handleTossPg,
    };
}

export default useTossPg;
